/* OU EN EST L'APPRENANT, ET OU IL VA ENSUITE — deux calculs purs, sortis de
 * UserContext le 02/10/2026 pour pouvoir les tester sans monter l'application.
 *
 *   indexDeReprise(formation)          l'ecran ou reprendre un module
 *   apresLaPartie(formation, actIndex) la suite quand une partie est finie :
 *                                      { formation (copie, parties/chapitres
 *                                      marques faits), index }
 *
 * Un index vaut { chapitre, partie, sequence }, ou { chapitre: "max", ... }
 * quand le module est termine (FinDeFormation).
 */
/* ⛔ B73 (02/10/2026) — UNE PARTIE OU UN CHAPITRE VIDE SE SAUTE.
   L'editeur laisse exister une partie sans aucun ecran (module 91 en base
   locale : chapitre « Pourquoi ? », partie « Modifier le titre », 0 ecran).
   Les deux calculs visaient alors « le premier ecran » de cette partie, qui
   n'existe pas : page entierement blanche, sans bouton ni erreur, apprenant
   bloque — a la reprise comme au passage a la suite.
   Une partie sans ecran n'a rien a faire : on la passe, et on la marque faite
   pour que la progression enregistree reste coherente. */
const aDesEcrans = (partie) => Array.isArray(partie?.sequence) && partie.sequence.length > 0
/* Le back ne pose pas `partie` sur un chapitre qui n'en a aucune
   (formationController, getAllInfos) : sans cette garde, le chargement levait
   une TypeError et ne se terminait jamais (relecture du 02/10/2026). */
const parties = (chapitre) => (Array.isArray(chapitre?.partie) ? chapitre.partie : [])

/* ⚠️ RIEN A FAIRE MAIS MODULE NON FERME : ON REPREND SUR LE DERNIER ECRAN FAIT,
   PAS SUR LE PREMIER (relecture du 02/10/2026). Cas de B73 : tous les ecrans
   faits, la suite n'etant qu'une partie vide. Revenir a l'ecran 0 obligeait a
   refaire tout le module (50 ecrans sur un gros module) pour que la fin se
   declenche ; sur le dernier ecran, un « Suivant » suffit. Meme logique pour
   une partie dont les ecrans sont faits mais qui n'a pas ete fermee
   (l'apprenant a quitte sur son dernier ecran) : on y revient sur ce dernier
   ecran, plus sur le premier. */
const dernierEcran = (formation, iChapMax = formation.chapitre.length - 1) => {
  for (let c = iChapMax; c >= 0; c--) {
    const ps = parties(formation.chapitre[c])
    for (let p = ps.length - 1; p >= 0; p--) {
      if (aDesEcrans(ps[p])) return { chapitre: c, partie: p, sequence: ps[p].sequence.length - 1 }
    }
  }
  return null
}

export const indexDeReprise = (formation) => {
  if (formation.isDone) return { chapitre: 'max', partie: 0, sequence: 0 }

  for (let iChap = 0; iChap < formation.chapitre.length; iChap++) {
    const chapitre = formation.chapitre[iChap]
    const ps = parties(chapitre)
    for (let iPart = 0; iPart < ps.length; iPart++) {
      const partie = ps[iPart]
      if (!aDesEcrans(partie)) continue
      for (let iSeq = 0; iSeq < partie.sequence.length; iSeq++) {
        if (partie.sequence[iSeq].isDone) continue
        return { chapitre: iChap, partie: iPart, sequence: iSeq }
      }
      if (!partie.isDone) return { chapitre: iChap, partie: iPart, sequence: partie.sequence.length - 1 }
    }
    if (!chapitre.isDone) {
      const d = dernierEcran(formation, iChap)
      if (d && d.chapitre === iChap) return d
    }
  }

  return dernierEcran(formation) || { chapitre: 0, partie: 0, sequence: 0 }
}

/* `partiesVidesSautees` : vrai si la suite a passe au moins une partie ou un
   chapitre vide. Avec une fin de module, c'est le signal qu'il faut RELIRE le
   module avant de le fermer (voir UserContext, onIndexChange). */
export const apresLaPartie = (formation, actIndex) => {
  const f = JSON.parse(JSON.stringify(formation))
  let c = actIndex.chapitre, p = actIndex.partie
  let partiesVidesSautees = false
  f.chapitre[c].partie[p].isDone = true
  for (;;) {
    p += 1
    if (p >= parties(f.chapitre[c]).length) {
      // chapitre termine
      f.chapitre[c].isDone = true
      c += 1; p = 0
      if (c >= f.chapitre.length) {
        // formation terminee
        f.isDone = true
        return { formation: f, index: { chapitre: 'max', partie: 0, sequence: 0 }, partiesVidesSautees }
      }
      if (parties(f.chapitre[c]).length === 0) { partiesVidesSautees = true; p = -1; continue }   // chapitre sans partie : on le passe
    }
    const partie = f.chapitre[c].partie[p]
    if (aDesEcrans(partie)) return { formation: f, index: { chapitre: c, partie: p, sequence: 0 }, partiesVidesSautees }
    partie.isDone = true   // partie vide : passee, et marquee faite
    partiesVidesSautees = true
  }
}

/* ⛔ PEUT-ON FERMER LE MODULE D'APRES SA RELECTURE ? (B73, relectures du
   02/10/2026 sur 20deedc). Rend la suite a enregistrer, ou null : ne rien
   fermer, la reprise posee par la relecture mene a ce qui reste a faire.
   On ne ferme que si le module RELU, celui du serveur :
   - est le meme module, pas encore ferme, avec le meme ecran a `position` ;
   - a cet ecran FAIT : sa reponse est en base. Sinon la fin partirait sans
     elle, et THE SCAN figerait un score avec cet ecran a 0 (le serveur compte
     sur la garantie B41 : quand isDone arrive, les reponses sont en base) ;
   - n'a rien a faire avant ni apres : sa reprise tombe sur `position`. Un
     ecran ajoute en fin de partie ou dans une partie anterieure y mene ;
   - et que la suite y est toujours une fin. */
export const fermetureApresRelecture = (relue, avant, position) => {
  if (!relue || !avant || relue.id !== avant.id || relue.isDone) return null
  const ecran = (f) => f?.chapitre?.[position.chapitre]?.partie?.[position.partie]?.sequence?.[position.sequence]
  if (ecran(relue)?.id == null || ecran(relue).id !== ecran(avant)?.id || !ecran(relue).isDone) return null
  const reprise = indexDeReprise(relue)
  if (reprise.chapitre !== position.chapitre || reprise.partie !== position.partie || reprise.sequence !== position.sequence) return null
  const suite = apresLaPartie(relue, position)
  return suite.index.chapitre === 'max' ? suite : null
}
